import React, { useContext, useState, useEffect } from 'react';
import { InputField, RadioGroupField } from '@trp-beacon-ui-kit/react-controls';
import { FlowContext } from '../../core/flow-engine';
import styles from '../FONASUMFTransferDetails/FONASUMFTransferDetails.css';
import SectionHeading from '../Typography/SectionHeading/SectionHeading';
import EditButton from '../Typography/EditButton/EditButton';
import {
  ProviderSelection,
  getProvider
} from '../FONASUMFTransferFunding/FONASUMFTransferFunding';
import { rolloverTypes } from '../../flows/shared/utils';
import ManualInstitutionReview from '../ManualInstitutionReview/ManualInstitutionReview';

const FONASURolloverFundingReview = ({
  custodianName,
  formerEmployer,
  rolloverType
}) => {
  return (
    <>
      {rolloverType !== '' ? (
        <div className="review-section">
          <div className="qv-row">
            <div className="qv-col">
              <div className="field-label">Rollover Type</div>
              <div className="field-value">{rolloverType}</div>
            </div>
            {rolloverType === 'DIRECT' && (
              <>
                <div className="qv-col">
                  <div className="field-label">Custodian</div>
                  <div className="field-value">{custodianName}</div>
                </div>
                <div className="qv-col">
                  <div className="field-label">Former Employer</div>
                  <div className="field-value">{formerEmployer}</div>
                </div>
              </>
            )}
          </div>
          <style jsx>{styles}</style>
        </div>
      ) : (
        <div>
          <ManualInstitutionReview readOnly />
        </div>
      )}
    </>
  );
};

const validateSection = (
  rolloverType,
  selectedCustodianName,
  formerEmployerRequired
) =>
  rolloverType === rolloverTypes.INDIRECT ||
  (selectedCustodianName !== '' && !formerEmployerRequired);

const checkIfIndirectIsOption = closedTickersArr => !!closedTickersArr?.length;

const FONASUMFRolloverFunding = ({
  editFunding,
  setEditFunding,
  showSuccess
}) => {
  const {
    appState: {
      store: {
        app: { custodian },
        loadedData: {
          institutions,
          formerEmployer,
          rolloverType,
          closedTickers,
          fundingEditMode
        }
      }
    },
    formProps,
    next
  } = useContext(FlowContext);

  const { custodianName } = custodian;

  const [selectedRolloverType, setSelectedRolloverType] = useState(
    rolloverType
  );
  const [selectedCustodian, setSelectedCustodian] = useState(custodian);
  const [formerEmployerRequired, setFormerEmployerRequired] = useState(true);

  const changeProvider = async provider => {
    const selectedProvider = getProvider(provider);
    setSelectedCustodian(selectedProvider);
  };

  const handleRolloverType = event => {
    if (event.target.value === rolloverTypes.DIRECT) {
      setSelectedRolloverType(rolloverTypes.DIRECT);
    } else {
      setSelectedRolloverType(rolloverTypes.INDIRECT);
    }
  };

  const handleClick = async () => {
    if (editFunding) {
      await next({
        section: 'rolloverType',
        custodian: selectedCustodian
      })();
    } else {
      await next('unlockRolloverFundingSection')();
    }
  };

  const handleFormerEmployer = ({ target: { value } }) => {
    if (value) {
      return setFormerEmployerRequired(false);
    }
    return setFormerEmployerRequired(true);
  };

  useEffect(() => {
    setEditFunding(fundingEditMode);
  }, [fundingEditMode]);

  useEffect(() => {
    if (formerEmployer !== '') {
      setFormerEmployerRequired(false);
    }
  }, []);

  const isValid = validateSection(
    selectedRolloverType,
    selectedCustodian.custodianName,
    formerEmployerRequired
  );

  const isIndirectNotAnOption = checkIfIndirectIsOption(closedTickers);

  const radioOptions = [
    {
      title: 'Direct',
      value: 'DIRECT',
      defaultChecked: selectedRolloverType === rolloverTypes.DIRECT
    },
    {
      title: 'Indirect',
      value: 'INDIRECT',
      defaultChecked: selectedRolloverType === rolloverTypes.INDIRECT,
      disabled: isIndirectNotAnOption
    }
  ];

  return (
    <div className="manual-institution">
      <div className="section-header">
        <SectionHeading text="My Rollover Details" />
      </div>

      {editFunding ? (
        <>
          <div className="assets-title-wrapper">
            <div className="assets-title">
              Direct : I have or will request a check payable to T. Rowe Price
              from my plan provider.
            </div>
          </div>
          <div className="assets-title-wrapper">
            <div className="assets-title">
              Indirect: I already have a check payable to me from my plan
              provider.
            </div>
          </div>
          <RadioGroupField
            title="Options"
            options={radioOptions}
            horizontal
            onChange={handleRolloverType}
            {...formProps.rolloverType}
            name="rolloverType"
          />
          {selectedRolloverType === rolloverTypes.DIRECT && (
            <>
              <div className="assets-title-wrapper">
                <ProviderSelection
                  custodian={selectedCustodian}
                  institutions={institutions}
                  changeProvider={changeProvider}
                />
              </div>
              <div className="qv-row">
                <InputField
                  type="text"
                  title="Former Employer"
                  {...formProps.formerEmployer}
                  maxLength="35"
                  defaultValue={formerEmployer}
                  onChange={handleFormerEmployer}
                />
              </div>
              {formerEmployerRequired && (
                <span className="error-msg">Please enter Former Employer</span>
              )}
            </>
          )}
        </>
      ) : (
        <FONASURolloverFundingReview
          custodianName={custodianName}
          formerEmployer={formerEmployer}
          rolloverType={rolloverType}
        />
      )}
      <EditButton
        onClick={handleClick}
        currentlyEditing={editFunding}
        disabled={!isValid || showSuccess}
      />
      <style jsx>{styles}</style>
    </div>
  );
};

export default FONASUMFRolloverFunding;
